import {A11yModule} from '@angular/cdk/a11y';
import {CommonModule} from '@angular/common';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import {APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {ComponentLibraryModule} from '@cxs-designsystem/angular';
import {
  ContactUsButtonModule,
  ContactUsLinkModule,
  DCPOccConfiguratorVariantAddToCartSerializer,
  DcpOccConfiguratorVariantUpdateCartEntrySerializer,
  DcpOccEndpointsService,
  FeatureTogglesService,
  IconButtonTooltipModule
} from '@dcp/configurator';
import {EffectsModule} from '@ngrx/effects';
import {StoreModule} from '@ngrx/store';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {CartPageLayoutHandler} from '@spartacus/cart/base/components';
import {ActiveCartService, MultiCartService} from '@spartacus/cart/base/core';
import {ProfileTagPushEventsService, TrackingService} from '@spartacus/cds';
import {CheckoutPaymentAdapter, PAYMENT_DETAILS_SERIALIZER,} from '@spartacus/checkout/base/core';
import {
  AuthHttpHeaderService,
  AuthInterceptor,
  AuthService,
  FeaturesConfigModule,
  OccEndpointsService,
  ProductPageMetaResolver,
  ProductSearchService,
  provideConfig,
  SearchPageMetaResolver,
} from '@spartacus/core';
import {
  OccConfiguratorVariantAddToCartSerializer,
  OccConfiguratorVariantUpdateCartEntrySerializer,
  VARIANT_CONFIGURATOR_ADD_TO_CART_SERIALIZER,
  VARIANT_CONFIGURATOR_UPDATE_CART_ENTRY_SERIALIZER,
} from '@spartacus/product-configurator/rulebased';
import {
  AppRoutingModule,
  JsonLdBuilderModule,
  OutletModule,
  OutletRefModule,
  PageSlotModule,
  ProductListComponentService,
  PwaModule,
  SeoModule,
  ViewConfig,
} from '@spartacus/storefront';
import {PersonalizationContextService} from '@spartacus/tracking/personalization/core';
import {UserAccountAdapter, UserAccountConnector,} from '@spartacus/user/account/core';
import {OccUserAccountAdapter} from '@spartacus/user/account/occ';
import {environment} from '../environments/environment';
import {AppComponent} from './app.component';
import {AuthModule} from './auth/auth.module';
import {DcpAuthHttpHeaderService} from './auth/services/dcp-auth-http-header.service';
import {DcpAuthService} from './auth/services/dcp-auth.service';
import {CartLayoutHandlerService} from './cart/cart-layout-handler.service';
import {CartModule} from './cart/cart.module';
import {ChatAdvisorComponent} from './chat-adivsor/chat-advisor.component';
import {DcpCheckoutModule} from './checkout/checkout.module';
import {ContactCtaModule} from './contact-cta/contact-cta.module';
import {ErrorModule} from './core/components/error/error.module';
import {GlobalErrorHandler} from './core/components/error/global-error-handler';
import {HomepageModule} from './homepage/homepage.module';
import {httpInterceptorProviders} from './http-interceptors';
import {DcpStorefrontComponent} from './layout/dcp-storefront/dcp-storefront.component';
import {LayoutModule} from './layout/layout.module';
import {PaymentModule} from './payment/payment.module';
import {DcpCheckoutPaymentAdapterService} from './payment/services/dcp-checkout-payment-adapter.service';
import {DcpPersonalizationContextService} from './personalization/dcp-personalization-context.service';
import {configuratorProviderList} from './product/configurator/providers';
import {ProductModule} from './product/product.module';
import {DcpProductPageMetaResolver} from './product/services/dcp-product-page-meta.resolver';
import {DCPSearchPageMetaResolver} from './product/services/search-page-meta.resolver';
import {DcpProfileTagPushEventsService} from './profiletag/dcp-profile-tag-push-events.service';
import {RegistrationModule} from './registration/registration.module';
import {SearchModule} from './search/search.module';
import {CustomProductListComponentService} from './search/services/custom-product-list-component.service';
import {DcpMultiCartService} from './services/cart/dcp-multi-cart.service';
import {
  NavigationComponentManagerModule
} from './shared-component-management/navigation-component-manager/navigation-component-manager.module';
import {FormlyDcpModule} from './shared/formly/formly-dcp.module';
import {LimitedTextModule} from './shared/limited-text/limited-text.module';
import {ContentLoadingIndicatorModule} from './shared/content-loading-indicator/content-loading-indicator.module';
import {DcpPageIndexModule} from './shared/page-index/dcp-page-index.module';
import {SelectTypeB2BModule} from './shared/select-type/select-type.module';
import {DcpSupportFormModule} from './shared/support-form/dcp-support-form.module';
import {DefaultModalModule} from './shared/modal/default-modal.module';
import {DcpGlobalMessageComponentModule} from './global-message/global-message.module';
import {SpartacusModule} from './spartacus/spartacus.module';
import {DcpPageLayoutModule} from './temp/page-layout/page-layout.module';
import {FixesModule} from './spartacus/fixes/fixes.module';
import {DcpTrackingService} from './services/dcp-tracking.service';
import {DcpPaymentDetailsSerializer} from './services/dcp-payment-details-serializer';
import {DcpPageMetaConfig} from './config/DcpPageMetaConfig';
import {DcpAdobeAnalyticsModule} from '@app/services/analytics/dcp-adobe-analytics.module';
import {DcpCurrencyService} from '@app/services/pricing/dcp-currency.service';
import {DCPAnonymousConsentsInterceptor} from '@app/services/consents/dcp-anonymous-consents-interceptor';
import {DcpProductSearchService} from '@app/search/services/dcp-product-search.service';
import {DcpActiveCartService} from '@app/services/dcp-active-cart.service';
import {DcpAuthInterceptor} from '@app/auth/interceptors/auth.interceptor';
import {
  CountrySwitchModalModule
} from '@app/layout/header/country-selector/country-switch-modal/country-switch-modal.module';
import {BillingModule} from '@app/billing/billing.module';
import {TestModule} from './test/test.module';
import {DigitalNativeCheckoutModule} from '@app/digital-native-checkout/digital-native-checkout.module';

import '@udex/web-components/dist/AccordionItem.js';
import '@udex/web-components/dist/Assets';
import '@udex/web-components/dist/Button.js';
import '@udex/web-components/dist/Checkbox.js';
import '@udex/web-components/dist/CountrySelector.js';
import '@udex/web-components/dist/DatePicker.js';
import '@udex/web-components/dist/Divider.js';
import '@udex/web-components/dist/Dropdown.js';
import '@udex/web-components/dist/FilterMenu.js';
import '@udex/web-components/dist/FilterMenuItem.js';
import '@udex/web-components/dist/HeroBanner.js';
import '@udex/web-components/dist/Link.js';
import '@udex/web-components/dist/List.js';
import '@udex/web-components/dist/ListItem.js';
import '@udex/web-components/dist/MessageStrip.js';
import '@udex/web-components/dist/Modal.js';
import '@udex/web-components/dist/Pagination.js';
import '@udex/web-components/dist/RadioButton.js';
import '@udex/web-components/dist/Search.js';
import '@udex/web-components/dist/SelectBox.js';
import '@udex/web-components/dist/SelectBoxGroupHeader.js';
import '@udex/web-components/dist/SelectBoxItem.js';
import '@udex/web-components/dist/SelectBoxSelectAll.js';
import '@udex/web-components/dist/StepInput.js';
import '@udex/web-components/dist/Switch.js';
import '@udex/web-components/dist/Tab.js';
import '@udex/web-components/dist/TabNavigation.js';
import '@udex/web-components/dist/Tag';
import '@udex/web-components/dist/TextField.js';
import '@udex/web-components/dist/Tile';
import '@udex/web-components/dist/Tooltip.js';
import '@udex/web-components/dist/features/SearchSuggestionsPopover.js';
import '@udex/web-components/dist/MastheadShell.js';
import '@udex/web-components/dist/MastheadItem.js';
import '@udex/web-components/dist/MastheadMenu.js';
import '@udex/web-components/dist/MastheadMobileMenu.js';
import '@udex/web-components/dist/MastheadProfileMenu.js';
import '@udex/web-components/dist/Search.js';
import '@ui5/webcomponents/dist/Icon.js';
import '@ui5/webcomponents/dist/Link';
import '@ui5/webcomponents/dist/ResponsivePopover.js';
import '@ui5/webcomponents/dist/Title';


const devImports: any[] = [];
if (!environment.production) {
  devImports.push(StoreDevtoolsModule.instrument({connectInZone: true}));
}

@NgModule({
  declarations: [AppComponent, DcpStorefrontComponent],
  imports: [
    // ANGULAR
    HttpClientModule,
    AppRoutingModule,
    A11yModule,
    StoreModule.forRoot({}),
    EffectsModule.forRoot([]),
    CommonModule,
    ReactiveFormsModule,

    // SPARTACUS
    SpartacusModule,
    PwaModule,
    SeoModule,
    OutletModule,
    OutletRefModule,
    PageSlotModule,
    FeaturesConfigModule,
    DcpGlobalMessageComponentModule,
    JsonLdBuilderModule,
    FixesModule,

    // DCP
    AuthModule,
    CartModule,
    DcpCheckoutModule,
    ContactCtaModule,
    DigitalNativeCheckoutModule,
    ErrorModule,
    HomepageModule,
    LayoutModule,
    PaymentModule,
    ProductModule,
    RegistrationModule,
    SearchModule,
    DcpPageLayoutModule,
    TestModule,
    BillingModule,

    // Analytics
    DcpAdobeAnalyticsModule,

    // SHARED
    ComponentLibraryModule,
    DcpPageIndexModule,
    ContentLoadingIndicatorModule,
    LimitedTextModule,
    NavigationComponentManagerModule,
    FormlyDcpModule,
    DcpSupportFormModule,
    SelectTypeB2BModule,
    IconButtonTooltipModule,
    DefaultModalModule,
    ContactUsButtonModule,
    ContactUsLinkModule,

    ...devImports,
    CountrySwitchModalModule,
    ChatAdvisorComponent,
  ],
  providers: [
    FeatureTogglesService,
    DcpCurrencyService,
    httpInterceptorProviders,
    OccUserAccountAdapter,
    UserAccountConnector,
    {
      // fetch feature toggles
      provide: APP_INITIALIZER,
      useFactory: (fts: FeatureTogglesService) => () =>
        fts.fetchFeatureToggles(),
      deps: [FeatureTogglesService],
      multi: true,
    },
    {provide: SearchPageMetaResolver, useExisting: DCPSearchPageMetaResolver},
    {
      provide: ProductPageMetaResolver,
      useExisting: DcpProductPageMetaResolver,
    },
    {provide: AuthService, useExisting: DcpAuthService},
    {provide: AuthInterceptor, useExisting: DcpAuthInterceptor},
    {
      provide: ProductListComponentService,
      useExisting: CustomProductListComponentService,
    },
    provideConfig({view: {defaultPageSize: 12}} as ViewConfig),
    {provide: CartPageLayoutHandler, useExisting: CartLayoutHandlerService},
    {
      provide: PersonalizationContextService,
      useExisting: DcpPersonalizationContextService,
    },
    {
      provide: ProfileTagPushEventsService,
      useExisting: DcpProfileTagPushEventsService,
    },
    {
      provide: CheckoutPaymentAdapter,
      useExisting: DcpCheckoutPaymentAdapterService,
    },
    {provide: UserAccountAdapter, useExisting: OccUserAccountAdapter},
    {
      provide: VARIANT_CONFIGURATOR_ADD_TO_CART_SERIALIZER,
      useExisting: DCPOccConfiguratorVariantAddToCartSerializer,
      multi: true,
    },
    {
      provide: VARIANT_CONFIGURATOR_UPDATE_CART_ENTRY_SERIALIZER,
      useExisting: DcpOccConfiguratorVariantUpdateCartEntrySerializer,
      multi: true,
    },
    {
      provide: OccConfiguratorVariantAddToCartSerializer,
      useExisting: DCPOccConfiguratorVariantAddToCartSerializer,
    },
    {
      provide: OccConfiguratorVariantUpdateCartEntrySerializer,
      useExisting: DcpOccConfiguratorVariantUpdateCartEntrySerializer,
    },
    {
      provide: PAYMENT_DETAILS_SERIALIZER,
      useExisting: DcpPaymentDetailsSerializer,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DCPAnonymousConsentsInterceptor,
      multi: true,
    },
    {provide: OccEndpointsService, useExisting: DcpOccEndpointsService},
    {provide: AuthHttpHeaderService, useExisting: DcpAuthHttpHeaderService},
    {provide: ErrorHandler, useClass: GlobalErrorHandler},
    provideConfig(DcpPageMetaConfig),
    {provide: ActiveCartService, useExisting: DcpActiveCartService},
    {provide: MultiCartService, useExisting: DcpMultiCartService},
    {provide: TrackingService, useExisting: DcpTrackingService},
    {provide: ProductSearchService, useExisting: DcpProductSearchService},
    ...configuratorProviderList,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
}
